import { Component, inject } from "@angular/core";
import { UiKitModule } from "@app/ui-kit.module";
import { Gas, LocalizeGasNamePipe } from "@domain/gas/gas";
import { GasService } from "@domain/gas/gas.service";
import { FlameDetectorConfiguration } from "@domain/project/configurations/flame-detector-configuration";
import { FgdsProject } from "@domain/project/fgds-project";
import { Floorplan } from "@domain/project/floorplan/floorplan";
import { ProjectEventType } from "@domain/project/project-event";
import { ModalService } from "@odx/angular/components/modal";
import { untilDestroyed } from "@odx/angular/utils";
import { ConfiguredProductComponent } from "@project/floorplanner/floorplan-menu/fgds/configured-product/configured-product.component";
import { FloorplanProductMenuComponent } from "@project/floorplanner/floorplan-menu/fgds/floorplan-product-menu/floorplan-product-menu.component";
import { FloorplanService } from "@project/floorplanner/floorplan.service";
import { ProjectStateService } from "@project/project-state.service";
import { DraggedItemType, FloorplanDragDropService } from "@utils/floorplan-drag-drop.service";
import { first, map, Observable, of } from "rxjs";

@Component({
  selector: "app-floorplan-flame-detector-menu",
  imports: [UiKitModule, FloorplanProductMenuComponent, ConfiguredProductComponent],
  providers: [LocalizeGasNamePipe],
  templateUrl: "./floorplan-flame-detector-menu.component.html",
  styleUrl: "./floorplan-flame-detector-menu.component.scss",
})
export class FloorplanFlameDetectorMenuComponent {
  private floorplanService = inject(FloorplanService);
  private gasService = inject(GasService);
  private projectStateService = inject(ProjectStateService);
  private localizeGasNamePipe = inject(LocalizeGasNamePipe);
  private dragDropService = inject(FloorplanDragDropService);
  private modalService = inject(ModalService);

  protected floorplan: Floorplan;
  protected flameDetectorSubtitles = new Map<FlameDetectorConfiguration, Observable<string>>();
  private takeUntilDestroyed = untilDestroyed();

  protected readonly tooltipTextShowCoverageArea: string = $localize`:@@floorplan.button.showCoverageArea:Überwachungsbereich einblenden`;
  protected readonly tooltipTextHideCoverageArea: string = $localize`:@@floorplan.button.hideCoverageArea:Überwachungsbereich ausblenden`;
  protected readonly tooltipTextFlameDetectors: string = $localize`:@@global.flameDetectors:Brandmelder`;
  private isReferenceMeasureSet$: Observable<boolean>;

  constructor() {
    this.floorplan = this.floorplanService.selectedFloorplan;
    this.isReferenceMeasureSet$ = this.floorplanService.isReferenceMeasureSet$;
    this.flameDetectors.forEach((flameDetector) => {
      this.flameDetectorSubtitles.set(flameDetector, this.subtitlePrefix$(flameDetector));
    });
    this.projectStateService.projectEvents$.pipe(this.takeUntilDestroyed()).subscribe((event) => {
      if (event.type === ProjectEventType.FLAME_DETECTOR_CONFIGURATION_DELETED) {
        this.flameDetectorSubtitles.delete(event.subject);
      }
    });
  }

  protected get flameDetectors(): readonly FlameDetectorConfiguration[] {
    if (this.floorplan.project instanceof FgdsProject) {
      return this.floorplan.project.flameDetectors;
    }
    return [];
  }

  protected addFlameDetector(flameDetector: FlameDetectorConfiguration) {
    this.floorplanService.addFlameDetector(flameDetector);
  }

  protected deleteFlameDetector(flameDetector: FlameDetectorConfiguration) {
    (this.floorplan.project as FgdsProject).deleteFlameDetector(flameDetector);
  }

  protected toggleCoverageAreaVisibility() {
    this.isReferenceMeasureSet$.pipe(first()).subscribe((isSet) => {
      if (!isSet) {
        this.modalService.create({
          title: $localize`:@@floorplan.coverageArea.noReferenceMeasure.title:Referenzmaß erforderlich`,
          content: $localize`:@@floorplan.coverageArea.noReferenceMeasure.description:Bitte setzen Sie zuerst ein Referenzmaß auf dem Grundriss, um den Überwachungsbereich anzeigen zu können.`,
          actions: [{ label: $localize`:@@global.ok:OK`, variant: "primary" }],
        });
        return;
      }
      this.floorplan.floorplanState.coverageAreasVisible = !this.floorplan.floorplanState.coverageAreasVisible;
    });
  }

  protected addPlaceholder() {
    this.floorplanService.addFlameDetectorPlaceholder();
  }

  protected drag(flameDetectorConfig: FlameDetectorConfiguration) {
    this.dragDropService.setDragged({ type: DraggedItemType.FLAME_DETECTOR, config: flameDetectorConfig });
  }

  protected dragPlaceholder() {
    this.dragDropService.setDragged({ type: DraggedItemType.FLAME_DETECTOR_PLACEHOLDER });
  }

  protected updateFlameDetectorsItemTextVisibility(visible: boolean) {
    this.floorplan.floorplanState.updateFlameDetectorsItemTextVisibility(visible);
  }

  private subtitlePrefix$(flameDetector: FlameDetectorConfiguration) {
    if (!flameDetector.substanceId) {
      return of("");
    }

    return this.gasService.getGasById(flameDetector.substanceId).pipe(
      map((substance: Gas | undefined) => {
        if (!substance) {
          return "";
        }
        const substanceName = this.localizeGasNamePipe.transform(substance);
        const rawCasNumber = substance.definition.casNumber;
        if (!rawCasNumber || substanceName === rawCasNumber) {
          return substanceName;
        }

        const casNumber = rawCasNumber.startsWith("CAS") ? rawCasNumber.substring(3).trim() : rawCasNumber;
        if (!casNumber || substanceName === casNumber) {
          return substanceName;
        }

        const formattedCasNumber = $localize`:@@floorplan.configuredProduct.substance.casNumber:CAS-Nr.: ${casNumber}`;
        return substanceName ? `${substanceName}, ${formattedCasNumber}` : formattedCasNumber;
      }),
    );
  }
}
